import { ApiService } from "@/services/api/ApiService";
import { DocEditLink, PublicDocEdit, PublicDocSaveResult } from "@/types/knowledge";

/**
 * Data access for the public document "magic edit link" feature.
 * Owner methods go through the authenticated API lane; public methods use the
 * no-login lane (token-authenticated), so they never require a session.
 */
export class DocEditLinkService {
  // ---------- Owner (authenticated) ----------

  static async get(documentId: string): Promise<DocEditLink | null> {
    const res = await ApiService.invoke<{ link: DocEditLink | null }>({
      resource: "doc-edit-link",
      action: "get",
      id: documentId,
    });
    return res?.link ?? null;
  }

  static async generate(documentId: string, rotate = false): Promise<DocEditLink | null> {
    const res = await ApiService.invoke<{ link: DocEditLink }>({
      resource: "doc-edit-link",
      action: "generate",
      id: documentId,
      data: { rotate },
    });
    return res?.link ?? null;
  }

  static async setPin(documentId: string, pin: string | null): Promise<DocEditLink | null> {
    const res = await ApiService.invoke<{ link: DocEditLink }>({
      resource: "doc-edit-link",
      action: "set-pin",
      id: documentId,
      data: { pin },
    });
    return res?.link ?? null;
  }

  static async revoke(documentId: string): Promise<boolean> {
    const res = await ApiService.invoke<{ success: boolean }>({
      resource: "doc-edit-link",
      action: "revoke",
      id: documentId,
    });
    return res?.success ?? false;
  }

  // ---------- Public (no login) ----------

  /** Returns null when the token is invalid / revoked so callers can show a branded 404. */
  static async getPublic(token: string): Promise<PublicDocEdit | null> {
    try {
      const res = await ApiService.invoke<PublicDocEdit>(
        { resource: "doc-edit-link", action: "public-get", data: { token } },
        "public",
      );
      return res ?? null;
    } catch (error) {
      console.error("Error loading edit link:", error);
      return null;
    }
  }

  /** Throws on failure so the caller can surface PIN / rate-limit messages. */
  static async savePublic(token: string, content: string, pin?: string): Promise<PublicDocSaveResult> {
    const res = await ApiService.invoke<PublicDocSaveResult>(
      {
        resource: "doc-edit-link",
        action: "public-update",
        data: { token, content, ...(pin ? { pin } : {}) },
      },
      "public",
    );
    return { success: res?.success ?? false, updatedAt: res?.updatedAt };
  }
}
